import { nookTitle } from "./nook-display.js";
export function foldTurn(events) {
    const citations = [];
    const steps = [];
    const turn = {
        threadId: null,
        messageId: null,
        title: null,
        error: null,
        answer: { content: "", citations, steps, shownNooks: [] },
    };
    for (const event of events) {
        switch (event.type) {
            case "turn":
                turn.threadId = event.threadId ?? null;
                turn.messageId = event.messageId;
                break;
            case "reasoning": {
                const last = steps.at(-1);
                if (last?.type === "reasoning" && last.stepId === event.stepId)
                    last.text += event.text;
                else
                    steps.push({ type: "reasoning", stepId: event.stepId, text: event.text });
                break;
            }
            case "tool_call":
                steps.push({ ...event, type: "tool", total: 0, nookIds: [], pending: true });
                break;
            case "tool_result": {
                const step = steps.find((candidate) => candidate.stepId === event.stepId);
                if (step?.type === "tool")
                    Object.assign(step, { total: event.total, nookIds: event.nookIds, pending: false });
                break;
            }
            case "text":
                turn.answer.content += event.text;
                break;
            case "citation":
                citations.push({ ref: event.ref, nookId: event.nookId, offset: event.offset });
                break;
            case "show_nooks":
                turn.answer.shownNooks = event.items.map(({ nook, why }) => ({ nook, nookId: nook.id, why }));
                break;
            case "title":
                turn.title = event.title;
                break;
            case "error":
                turn.error = { code: event.code, message: event.message };
                break;
        }
    }
    return turn;
}
/**
 * Each source's number, which its citation markers carry too: cited sources in
 * the order the prose first cites them, then uncited cards. Stable mid-stream,
 * before the cards arrive.
 */
export const sourceNumbers = (answer) => new Map([...new Set([...answer.citations, ...answer.shownNooks].map((source) => source.nookId))].map((id, index) => [id, index + 1]));
/** The source cards in their numbered order. */
export function numberedSources(answer) {
    const position = sourceNumbers(answer);
    return [...answer.shownNooks].sort((a, b) => position.get(a.nookId) - position.get(b.nookId));
}
export const sourceTitles = (answer) => new Map(answer.shownNooks.flatMap((card) => (card.nook ? [[card.nookId, nookTitle(card.nook)]] : [])));
/** The answer with each citation put back as the markdown `marker` draws for its number. */
export function citedProse(answer, marker) {
    const position = sourceNumbers(answer);
    let prose = "";
    let at = 0;
    for (const citation of answer.citations) {
        prose += answer.content.slice(at, citation.offset) + marker(position.get(citation.nookId), citation.nookId);
        at = citation.offset;
    }
    return prose + answer.content.slice(at);
}
/** Consecutive `get_nook` reads become one group; every other step stands alone. */
export function groupReads(steps) {
    const read = (step) => step?.type === "tool" && step.tool === "get_nook";
    return steps.reduce((groups, step) => {
        const last = groups.at(-1);
        if (last && read(step) && read(last[0]))
            last.push(step);
        else
            groups.push([step]);
        return groups;
    }, []);
}
const dayMonth = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });
function dateRange(args) {
    const day = (value) => {
        const date = typeof value === "string" ? new Date(value) : null;
        return date && !Number.isNaN(date.getTime()) ? dayMonth.format(date) : null;
    };
    const [from, to] = [day(args["from"]), day(args["to"])];
    if (from && to)
        return ` · ${from} – ${to}`;
    if (from)
        return ` · since ${from}`;
    return to ? ` · until ${to}` : "";
}
export function stepLabel(step, titles) {
    const found = step.pending ? "…" : ` → ${step.total} ${step.total === 1 ? "nook" : "nooks"}`;
    switch (step.tool) {
        case "search_nooks":
            return `Searched your library for “${String(step.args["query"] ?? "")}”${dateRange(step.args)}${found}`;
        case "list_nooks":
            return `Listed your nooks${dateRange(step.args)}${found}`;
        case "list_tags":
            return step.pending ? "Checking your topics…" : "Checked your topics";
        case "get_nook": {
            const title = step.nookIds[0] ? titles.get(step.nookIds[0]) : undefined;
            return step.pending ? "Reading…" : title ? `Read “${title}”` : "Read a nook";
        }
    }
}
/** The non-tag filters behind a step, for its expanded row. */
export function stepDetails(args) {
    const list = (value) => (Array.isArray(value) ? value.join(", ") : value ? String(value) : null);
    return [
        list(args["platform"]) && `platform: ${list(args["platform"])}`,
        list(args["source"]) && `saved from: ${list(args["source"])}`,
        list(args["has"]) && `with: ${list(args["has"])}`,
    ].filter((detail) => Boolean(detail));
}
//# sourceMappingURL=ask-turn.js.map